/**
 * The order source behind the ranked position pop-out (SE-8393, PV23 · D9,
 * Technical Approach 1). The pop-out used to read the watchlist controller
 * directly; this is the narrower contract it reads now, so the scenario
 * pitch can rank a slot through the same organism: the watchlist implements
 * it through {@link watchlistSlotOrder} over its loaded group (behaviour
 * unchanged), the scenario through `useScenarioSlotPopout`.
 * @module features/scouting/hooks/slotOrderController
 */
import type { PositionPopoutRow } from "@/features/scouting/components/molecules/WatchlistPositionPopoutRow";
import type { SquadOverlayPlayer } from "@/features/scouting/utils/watchlistPitchOverlay";

import type { WatchlistPreferenceOrderController } from "./useWatchlistPreferenceOrder";
import {
  orderItemsByPreference,
  type WatchlistPreferenceStatus,
} from "./watchlistPreferenceOrderState";

/** One ranked row shared by watchlist and scenario pop-outs. */
export type SlotOrderRow = PositionPopoutRow;

/** What the pop-out needs from whoever owns the slot's order. */
export interface SlotOrderController {
  /** The slot's complete group in its current (projected) order. */
  readonly rows: readonly SlotOrderRow[];
  readonly status: WatchlistPreferenceStatus;
  /** Whether this viewer may reorder the group right now. */
  readonly canReorder: boolean;
  /** Writes the group's new order, as the drag or the move menu produced it. */
  readonly reorder: (
    orderedPlayerIds: readonly string[]
  ) => Promise<void> | void;
}

/** One loaded watchlist group member, as the Pitch read returns it. */
export interface WatchlistGroupMember {
  readonly playerId: string;
  readonly displayName: string;
  readonly rank: number;
  readonly gpr?: number | null;
  readonly club?: string | null;
}

/**
 * The watchlist's controller for one loaded group: rows in saved preference
 * order, ranked 1..N within the position; reorderable once the whole group is
 * loaded and the list is editable.
 * @param members - Loaded group members
 * @param preferenceOrder - The list's preference-order controller, when any
 * @param listComplete - Whether every page of the group has loaded
 * @returns The slot-order controller the pop-out reads
 */
export const watchlistSlotOrder = (
  members: readonly WatchlistGroupMember[],
  preferenceOrder: WatchlistPreferenceOrderController | undefined,
  listComplete: boolean
): SlotOrderController => {
  const ordered = preferenceOrder
    ? orderItemsByPreference(
        members,
        preferenceOrder.orderedPlayerIds,
        member => member.playerId
      )
    : members;
  return {
    // SE-8535: ranked within the position (the title reads "ranked 1–N"),
    // never the list-wide preference rank.
    rows: ordered.map((member, index) => ({
      playerId: member.playerId,
      displayName: member.displayName,
      rank: index + 1,
      gpr: member.gpr ?? null,
      club: member.club?.trim() || null,
    })),
    status: preferenceOrder?.status ?? "idle",
    canReorder: Boolean(preferenceOrder?.canEdit) && listComplete,
    reorder: orderedPlayerIds =>
      preferenceOrder?.reorderVisible(orderedPlayerIds),
  };
};

/** Nothing at the slot: a stable empty list. */
const NO_SQUAD_ROWS: readonly SlotOrderRow[] = [];

/**
 * SE-8535: the watchlist pop-out's ● squad rows — read-only and unranked
 * (`rank: null`), in the order the card draws them. The squad is not part
 * of the list's order, so it never takes a place in the 1..N ranking.
 * @param players - The squad players placed at the slot, if any
 * @returns The rows the pop-out lists above the targets
 */
export const squadPopoutRows = (
  players: readonly SquadOverlayPlayer[] | undefined
): readonly SlotOrderRow[] =>
  players?.length
    ? players.map(player => ({
        playerId: player.id,
        displayName: player.displayName,
        rank: null,
        gpr: player.gpr ?? null,
        club: null,
        inSquad: true,
      }))
    : NO_SQUAD_ROWS;
